/**
 * assetSyncStore — 에셋 sync 진행률 UI 상태 (non-React native/assetDelivery → React 브릿지).
 *
 * native/assetDelivery.ts 가 플러그인 assetProgress 이벤트를 이 store 로 흘리고,
 * hooks/useAssetSync (온보딩 진행률 UI 등)가 구독해 렌더한다. otaStore 와 같은 패턴.
 * 상태 정본은 네이티브(sidecar) — 이 store 는 표시용 미러일 뿐이다 (계약 §1 코어 규칙 4).
 */
import { create } from "zustand";
import type {
  AssetErrorCode,
  AssetProgressEvent,
} from "@duckflex/capacitor-asset-delivery";

/** UI 표시용 sync 상태 — 플러그인 state + idle(시작 전). */
export type AssetSyncStatus =
  | "idle"
  | "checking"
  | "downloading"
  | "verifying"
  | "activating"
  | "done"
  | "error";

export interface AssetSyncState {
  status: AssetSyncStatus;
  /** sync 전체 합산 바이트 (진행률 이벤트 미러). */
  bytesReceived: number;
  bytesTotal: number;
  retryCount: number;
  /** 현재 다운로드 중 파일 (표시용, 없으면 null). */
  currentKind: string | null;
  errorCode: AssetErrorCode | null;
  /** sync 성공 시 활성 릴리스 표시 버전. */
  releaseVer: string | null;
}

interface AssetSyncActions {
  begin: () => void;
  applyEvent: (event: AssetProgressEvent) => void;
  finishDone: (releaseVer: string) => void;
  fail: (code: AssetErrorCode | null) => void;
  reset: () => void;
}

const initialState: AssetSyncState = {
  status: "idle",
  bytesReceived: 0,
  bytesTotal: 0,
  retryCount: 0,
  currentKind: null,
  errorCode: null,
  releaseVer: null,
};

export const useAssetSyncStore = create<AssetSyncState & AssetSyncActions>((set) => ({
  ...initialState,

  begin: () => set({ ...initialState, status: "checking" }),

  applyEvent: (event) =>
    set({
      status: event.state,
      bytesReceived: event.bytesReceived,
      bytesTotal: event.bytesTotal,
      retryCount: event.retryCount,
      currentKind: event.assetKind ?? null,
      errorCode: event.errorCode ?? null,
    }),

  finishDone: (releaseVer) => set({ status: "done", errorCode: null, releaseVer }),

  fail: (code) => set({ status: "error", errorCode: code }),

  reset: () => set(initialState),
}));

/** 0~1 진행률. 총량 미확정(checking 등)이면 null — UI 는 indeterminate 로 표시. */
export function selectAssetSyncProgress(state: AssetSyncState): number | null {
  if (state.status === "done") return 1;
  if (state.bytesTotal <= 0) return null;
  return Math.min(1, state.bytesReceived / state.bytesTotal);
}
